/**
 * The Brain transports' live lane: one Session followed over Server-Sent Events.
 *
 * A reconnect resumes after the highest sequence with nothing missing before
 * it, since two writers can publish out of order. Only a reconnect delivers its
 * `stream` event: the first follow's reply-so-far is restored by the chat hook.
 */

import { useCallback, useEffect, useRef } from 'react';
import { followSession } from './sessionFollow';
import type { FollowedFrame, FollowedOutput } from './sessionFollow';

/** One frame the follow delivers, in the shape every handler reads. */
export interface WSMessage {
  type: string;
  channel?: string;
  id?: string;
  data?: Record<string, unknown>;
  message?: string;
}

export type MessageHandler = (msg: WSMessage) => void;

/** What a Brain client offers its live lane. */
export interface FollowingClient {
  /** Open one follow of a Session from a cursor. */
  openFollow(sessionId: string, afterSequence: number | undefined, signal: AbortSignal): Promise<Response>;
  /**
   * The last sequence of the Session this client already restored, if it did:
   * the first follow starts after it rather than replaying what is drawn.
   */
  restoredThrough(sessionId: string): number | undefined;
}

interface UseSessionFollowReturn {
  subscribe: (sessionId: string) => void;
  addMessageHandler: (key: string, handler: MessageHandler) => void;
  removeMessageHandler: (key: string) => void;
}

/** The frame a recorded output was published as. */
export function frameFromOutput(sessionId: string, output: FollowedOutput): FollowedFrame {
  return {
    event: output.kind,
    session_id: sessionId,
    sequence: output.sequence,
    output_id: output.output_id,
    data: output.payload,
  };
}

/** Follow the subscribed Session through `client`; inert when it is null. */
export function useSessionFollow(client: FollowingClient | null): UseSessionFollowReturn {
  const handlersRef = useRef<Map<string, MessageHandler>>(new Map());
  const followingRef = useRef<{ sessionId: string; stop: () => void } | null>(null);
  const clientRef = useRef(client);
  useEffect(() => {
    clientRef.current = client;
  }, [client]);

  const deliver = useCallback((sessionId: string, frame: FollowedFrame) => {
    const message: WSMessage = { type: 'message', channel: 'brain_session', id: sessionId, data: { ...frame } };
    for (const handler of handlersRef.current.values()) handler(message);
  }, []);

  const subscribe = useCallback(
    (sessionId: string) => {
      const following = clientRef.current;
      if (!following || followingRef.current?.sessionId === sessionId) return;
      followingRef.current?.stop();
      // Everything at or below `restored` is drawn already; `delivered` is what
      // this follow has handed on since.
      const restored = following.restoredThrough(sessionId);
      const delivered = new Set<number>();
      const cursor = () => {
        let held = restored ?? -1;
        while (delivered.has(held + 1)) held += 1;
        return held < 0 ? undefined : held;
      };
      const isNew = (sequence: number) =>
        !(restored !== undefined && sequence <= restored) && !delivered.has(sequence);
      const deliverRow = (output: FollowedOutput) => {
        if (!isNew(output.sequence)) return;
        delivered.add(output.sequence);
        deliver(sessionId, frameFromOutput(sessionId, output));
      };
      let opened = 0;
      // A reconnect's replay, held until its `stream` event says how the turn stands.
      let replay: FollowedOutput[] | null = null;
      const stop = followSession({
        open: (afterSequence, signal) => following.openFollow(sessionId, afterSequence, signal),
        cursor,
        handlers: {
          onOpen: () => {
            opened += 1;
            replay = opened > 1 ? [] : null;
          },
          onOutput: (output) => {
            if (replay) replay.push(output);
            else deliverRow(output);
          },
          onStream: (streamed) => {
            const rows: FollowedOutput[] = replay ?? [];
            if (replay === null) return;
            replay = null;
            if (streamed.active) {
              rows.forEach(deliverRow);
              deliver(sessionId, {
                event: 'stream',
                session_id: sessionId,
                data: { turn_event_id: streamed.turn_event_id, text: streamed.text, next_index: streamed.next_index },
              });
              return;
            }
            rows.filter((row) => row.kind !== 'quick_actions').forEach(deliverRow);
            deliver(sessionId, { event: 'turn_complete', session_id: sessionId });
            rows.filter((row) => row.kind === 'quick_actions').forEach(deliverRow);
          },
          onFrame: (frame) => {
            if (frame.sequence !== undefined) {
              if (!isNew(frame.sequence)) return;
              delivered.add(frame.sequence);
            }
            deliver(sessionId, frame);
          },
        },
      });
      followingRef.current = { sessionId, stop };
    },
    [deliver],
  );

  useEffect(
    () => () => {
      followingRef.current?.stop();
      followingRef.current = null;
    },
    [],
  );

  const addMessageHandler = useCallback((key: string, handler: MessageHandler) => {
    handlersRef.current.set(key, handler);
  }, []);

  const removeMessageHandler = useCallback((key: string) => {
    handlersRef.current.delete(key);
  }, []);

  return { subscribe, addMessageHandler, removeMessageHandler };
}
